import { PlainDate, genPlainDateList } from "@sa-nu/shared-utils/date";

/** RoomType.availabilities.nights の1泊分（FE 用に必要最小限へ正規化したシェイプ）。 */
export type NightAvailability = {
  night: PlainDate;
  /** その夜に空いている accommodation の id 一覧（EV/日付フィルタ適用済みのものが渡る前提） */
  availableAccommodationIds: readonly string[];
};

/** night.toString() → Set<id> のルックアップマップを構築する */
function buildNightMap(
  nights: readonly NightAvailability[],
): Map<string, Set<string>> {
  return new Map(
    nights.map((n) => [
      n.night.toString(),
      new Set(n.availableAccommodationIds),
    ]),
  );
}

/** 2つの Set の積集合を返す */
function intersect(a: Set<string>, b: Set<string>): Set<string> {
  return new Set([...a].filter((id) => b.has(id)));
}

/**
 * CI(firstNight)..CO-1(lastNight) の全泊で空いている accommodation の積集合から 1 室を選んで返す。
 * 第一段階は部屋を跨いで交換しないため「全泊同一の空室」が必要。安定のため id 昇順の先頭を返す。
 * 積集合が空、または範囲内に欠けた夜（nights に存在しない夜）があれば null。
 * （best-fit のタイブレークは後続。まずは安定した素朴版。）
 */
export function resolveAccommodationForRoomType(args: {
  nights: readonly NightAvailability[];
  firstNight: PlainDate;
  lastNight: PlainDate;
}): string | null {
  const { nights, firstNight, lastNight } = args;

  // lastNight < firstNight は無効
  if (PlainDate.compare(lastNight, firstNight) < 0) {
    return null;
  }

  const map = buildNightMap(nights);
  const dateList = genPlainDateList(firstNight, lastNight);

  // 各夜の id-Set を取得し、欠けた夜（ギャップ）を type-guard で落とす。
  // 1夜でも欠けていれば全泊を満たせないので null。
  const presentSets = dateList
    .map((d) => map.get(d.toString()))
    .filter((s): s is Set<string> => s !== undefined);
  if (presentSets.length !== dateList.length) {
    return null;
  }

  // 全泊の積集合を reduce で計算（dateList は firstNight<=lastNight 保証で非空）
  const intersection = presentSets.reduce((acc, s) => intersect(acc, s));

  if (intersection.size === 0) {
    return null;
  }

  // id 昇順の先頭を返す（安定したタイブレーク）
  return [...intersection].sort()[0] ?? null;
}

/**
 * 指定 CI(firstNight) に対して選択可能な CO 日(= lastNight+1)の一覧を返す（昇順）。
 * CO が valid な条件: CI から CO-1 までの全ての夜が nights に連続して存在し、かつ
 * それら全泊の availableAccommodationIds の積集合が非空であること。
 * 連続性が途切れた / 積集合が空になった時点でそれ以上は伸ばせない（打ち切り）。
 * CI 自身が nights に無い or その夜が空室ゼロなら空配列。
 */
export function selectableCheckoutDates(args: {
  nights: readonly NightAvailability[];
  firstNight: PlainDate;
}): PlainDate[] {
  const { nights, firstNight } = args;

  if (nights.length === 0) return [];

  const map = buildNightMap(nights);
  const lastNightEntry = nights[nights.length - 1];
  // nights.length > 0 is guaranteed above; lastNightEntry is always defined
  if (lastNightEntry === undefined) return [];

  const maxNight = lastNightEntry.night;
  const result: PlainDate[] = [];
  let running: Set<string> | null = null;

  // Iterate night-by-night from firstNight up to maxNight (finite bound).
  // Stop early when a gap or empty intersection is encountered.
  let current = firstNight;
  while (PlainDate.compare(current, maxNight) <= 0) {
    const ids = map.get(current.toString());
    if (ids === undefined || ids.size === 0) break;
    running = running === null ? new Set(ids) : intersect(running, ids);
    if (running.size === 0) break;
    // CO = current + 1（current は stay night であり CO = lastNight + 1）
    result.push(current.add({ days: 1 }));
    current = current.add({ days: 1 });
  }

  return result;
}
